import axios from 'axios';
import { getApiBaseUrl } from '../config/config';
import { getIdToken } from './authService';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  async (config) => {
    // Get JWT token from Cognito session
    const token = await getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Extract data from standard API response format
    if (response.data && response.data.success) {
      return response.data.data;
    }
    return response.data;
  },
  (error) => {
    if (error.response) {
      // Server responded with error
      const errorData = error.response.data;
      if (errorData && errorData.error) {
        throw new Error(errorData.error.message || 'An error occurred');
      }
    }
    throw error;
  }
);

/**
 * Get user's conversations
 * @param {Object} params - Query parameters (page, limit, etc.)
 * @returns {Promise<Array>} List of conversations
 */
export const getConversations = async (params = {}) => {
  try {
    const response = await apiClient.get('/v1/messages/conversations', { params });
    return response;
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
};

/**
 * Get messages in a thread
 * @param {string} threadId - Thread ID
 * @param {Object} params - Query parameters (page, limit, etc.)
 * @returns {Promise<Array>} List of messages
 */
export const getThreadMessages = async (threadId, params = {}) => {
  try {
    const response = await apiClient.get(`/v1/messages/threads/${threadId}`, { params });
    return response;
  } catch (error) {
    console.error('Error fetching thread messages:', error);
    throw error;
  }
};

/**
 * Send a new message
 * @param {Object} messageData - Message data
 * @param {string} messageData.content - Message content
 * @param {Array<string>} messageData.recipients - Recipient user IDs
 * @param {string} [messageData.threadId] - Optional thread ID for replies
 * @returns {Promise<Object>} Created message
 */
export const sendMessage = async (messageData) => {
  try {
    const response = await apiClient.post('/v1/messages', messageData);
    return response;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Create a new message/conversation
 * @param {Object} messageData - Message data
 * @returns {Promise<Object>} Created thread
 */
export const createMessage = async (messageData) => {
  try {
    const response = await apiClient.post('/v1/messages', messageData);
    return response;
  } catch (error) {
    console.error('Error creating message:', error);
    throw error;
  }
};

/**
 * Search users for messaging
 * @param {string} query - Search query
 * @param {Object} params - Additional search parameters
 * @returns {Promise<Array>} List of users
 */
export const searchUsers = async (query, params = {}) => {
  try {
    const response = await apiClient.get('/v1/users/search', {
      params: { query, ...params }
    });
    // The interceptor already extracts response.data.data, so response is the data object
    // which contains { users: [...], pagination: {...} }
    return response.users || [];
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

/**
 * Update message status
 * @param {string} messageId - Message ID
 * @param {string} status - New status (sent, delivered, read)
 * @returns {Promise<Object>} Updated message
 */
export const updateMessageStatus = async (messageId, status) => {
  try {
    const response = await apiClient.put(`/v1/messages/${messageId}/status`, { status });
    return response;
  } catch (error) {
    console.error('Error updating message status:', error);
    throw error;
  }
};

/**
 * Delete a message
 * @param {string} messageId - Message ID
 * @returns {Promise<void>}
 */
export const deleteMessage = async (messageId) => {
  try {
    await apiClient.delete(`/v1/messages/${messageId}`);
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

/**
 * Search messages
 * @param {string} query - Search query
 * @param {Object} filters - Search filters
 * @returns {Promise<Array>} Search results
 */
export const searchMessages = async (query, filters = {}) => {
  try {
    const response = await apiClient.get('/v1/messages/search', {
      params: { query, ...filters }
    });
    return response;
  } catch (error) {
    console.error('Error searching messages:', error);
    throw error;
  }
};

/**
 * Get notifications for the current user
 * @param {Object} params - Query parameters (page, limit, unreadOnly)
 * @returns {Promise<Array>} List of notifications
 */
export const getNotifications = async (params = {}) => {
  try {
    // Notifications are messages with type 'notification'
    const response = await apiClient.get('/v1/messages/conversations', {
      params: { ...params, messageType: 'notification' }
    });
    return response;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Get announcements for the current user
 * @param {Object} params - Query parameters (page, limit)
 * @returns {Promise<Array>} List of announcements
 */
export const getAnnouncements = async (params = {}) => {
  try {
    // Announcements are messages with type 'announcement'
    const response = await apiClient.get('/v1/messages/conversations', {
      params: { ...params, messageType: 'announcement' }
    });
    return response;
  } catch (error) {
    console.error('Error fetching announcements:', error);
    throw error;
  }
};

/**
 * Get notification preferences for the current user
 * @returns {Promise<Object>} User notification preferences
 */
export const getNotificationPreferences = async () => {
  try {
    // This would call a preferences endpoint when implemented
    // For now, return default preferences
    return {
      emailNotifications: true,
      inAppNotifications: true,
      notificationTypes: {
        eventAssignment: true,
        responseSubmission: true,
        deadlineReminder: true,
        systemMaintenance: true,
        announcements: true
      }
    };
  } catch (error) {
    console.error('Error fetching notification preferences:', error);
    throw error;
  }
};

/**
 * Update notification preferences for the current user
 * @param {Object} preferences - Updated preferences
 * @returns {Promise<Object>} Updated preferences
 */
export const updateNotificationPreferences = async (preferences) => {
  try {
    // This would call a preferences endpoint when implemented
    // For now, just return the preferences
    console.log('Updating notification preferences:', preferences);
    return preferences;
  } catch (error) {
    console.error('Error updating notification preferences:', error);
    throw error;
  }
};

export default {
  getConversations,
  getThreadMessages,
  sendMessage,
  createMessage,
  searchUsers,
  updateMessageStatus,
  deleteMessage,
  searchMessages,
  getNotifications,
  getAnnouncements,
  getNotificationPreferences,
  updateNotificationPreferences
};

/**
 * Get reported messages for admin review
 * @param {Object} params - Query parameters (page, limit, status)
 * @returns {Promise<Array>} List of reported messages
 */
export const getReportedMessages = async (params = {}) => {
  try {
    const response = await apiClient.get('/v1/admin/messages/reports', { params });
    return response;
  } catch (error) {
    console.error('Error fetching reported messages:', error);
    throw error;
  }
};

/**
 * Report a message for moderation
 * @param {Object} reportData - Report data
 * @param {string} reportData.messageId - Message ID to report
 * @param {string} reportData.reason - Reason for report
 * @param {string} reportData.description - Detailed description
 * @returns {Promise<Object>} Created report
 */
export const reportMessage = async (reportData) => {
  try {
    const response = await apiClient.post('/v1/messages/report', reportData);
    return response;
  } catch (error) {
    console.error('Error reporting message:', error);
    throw error;
  }
};

/**
 * Execute moderation action on a reported message
 * @param {Object} actionData - Moderation action data
 * @param {string} actionData.reportId - Report ID
 * @param {string} actionData.messageId - Message ID
 * @param {string} actionData.action - Action type (remove, restrict, dismiss)
 * @param {string} actionData.reason - Reason for action
 * @returns {Promise<Object>} Action result
 */
export const executeModerationAction = async (actionData) => {
  try {
    const response = await apiClient.post('/v1/admin/messages/moderate', actionData);
    return response;
  } catch (error) {
    console.error('Error executing moderation action:', error);
    throw error;
  }
};

/**
 * Dismiss a message report
 * @param {string} reportId - Report ID
 * @returns {Promise<void>}
 */
export const dismissReport = async (reportId) => {
  try {
    await apiClient.put(`/v1/admin/messages/reports/${reportId}/dismiss`);
  } catch (error) {
    console.error('Error dismissing report:', error);
    throw error;
  }
};

/**
 * Create an announcement
 * @param {Object} announcementData - Announcement data
 * @param {string} announcementData.title - Announcement title
 * @param {string} announcementData.content - Announcement content
 * @param {string} announcementData.targetAudience - Target audience (all, speakers, attendees, admins)
 * @param {string} announcementData.priority - Priority level (normal, high, urgent)
 * @param {string} [announcementData.scheduledAt] - Optional scheduled delivery time
 * @returns {Promise<Object>} Created announcement
 */
export const createAnnouncement = async (announcementData) => {
  try {
    const response = await apiClient.post('/v1/admin/announcements', announcementData);
    return response;
  } catch (error) {
    console.error('Error creating announcement:', error);
    throw error;
  }
};

/**
 * Get announcement history for admin
 * @param {Object} params - Query parameters (page, limit, status)
 * @returns {Promise<Array>} List of announcements with delivery stats
 */
export const getAnnouncementHistory = async (params = {}) => {
  try {
    const response = await apiClient.get('/v1/admin/announcements/history', { params });
    return response;
  } catch (error) {
    console.error('Error fetching announcement history:', error);
    throw error;
  }
};

/**
 * Get announcement statistics
 * @param {string} announcementId - Announcement ID
 * @returns {Promise<Object>} Announcement statistics
 */
export const getAnnouncementStats = async (announcementId) => {
  try {
    const response = await apiClient.get(`/v1/admin/announcements/${announcementId}/stats`);
    return response;
  } catch (error) {
    console.error('Error fetching announcement stats:', error);
    throw error;
  }
};

/**
 * Get users with moderation status
 * @param {Object} params - Query parameters (page, limit, search, status)
 * @returns {Promise<Array>} List of users with moderation info
 */
export const getUsersWithModerationStatus = async (params = {}) => {
  try {
    const response = await apiClient.get('/v1/admin/users/moderation', { params });
    return response;
  } catch (error) {
    console.error('Error fetching users with moderation status:', error);
    throw error;
  }
};

/**
 * Restrict user messaging privileges
 * @param {Object} restrictionData - Restriction data
 * @param {string} restrictionData.userId - User ID to restrict
 * @param {string} restrictionData.reason - Reason for restriction
 * @param {string} restrictionData.durationType - Duration type (temporary, permanent)
 * @param {string} [restrictionData.restrictionEndDate] - End date for temporary restrictions
 * @returns {Promise<Object>} Restriction result
 */
export const restrictUserMessaging = async (restrictionData) => {
  try {
    const response = await apiClient.post('/v1/admin/users/restrict', restrictionData);
    return response;
  } catch (error) {
    console.error('Error restricting user:', error);
    throw error;
  }
};

/**
 * Remove user messaging restriction
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const removeUserRestriction = async (userId) => {
  try {
    await apiClient.delete(`/v1/admin/users/${userId}/restriction`);
  } catch (error) {
    console.error('Error removing user restriction:', error);
    throw error;
  }
};

/**
 * Get user moderation history
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Moderation history
 */
export const getUserModerationHistory = async (userId) => {
  try {
    const response = await apiClient.get(`/v1/admin/users/${userId}/moderation-history`);
    return response;
  } catch (error) {
    console.error('Error fetching moderation history:', error);
    throw error;
  }
};
